import { useCallback, useMemo, useState, type ReactNode } from 'react';
import styles from './PageShell.module.css';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { FilterChips, type Chip, type ChipKind } from './FilterChips';
import { loadActiveProfile, loadSession, saveActiveProfile } from '@/lib/auth';
import { titleCase } from '@/lib/format';
import { DEFAULT_RANGE, type DateRange } from '@/lib/dateRange';
import type { ResolvedWindow } from '@/lib/pages.api';

/**
 * The chrome every page shares: nav, top bar, filter chips, content column.
 *
 * Availability composes these three itself, which was right when it was the
 * only page. With six, the composition — brand picker wired to the session,
 * date range echoing the resolved window, chips clearing the levels they
 * narrow — is the same on all of them, and a copy per page is six places for
 * the drill-down-clearing rule to drift.
 *
 * Availability is deliberately NOT migrated onto this. It is pinned to a
 * pixel-diffed Figma comp, and re-parenting it to share a shell would be a
 * layout change made for tidiness, measured against a reference that has not
 * changed.
 */

export interface Scope {
  category?: string;
  city?: string;
  sku?: string;
}

export interface PageShellRender {
  /** The tenant every request must be scoped to. Empty means no grants. */
  customer: string;
  scope: Scope;
  range: DateRange;
  setScope: (next: Scope) => void;
  /** True when the sign-in holds at least one Blinkit brand. */
  hasGrants: boolean;
}

const PREFERRED_CUSTOMER = import.meta.env.VITE_CUSTOMER_NAME ?? 'Glenmark';

export function PageShell({
  title,
  activeNavId,
  activeSection,
  onNavigate,
  onSignOut,
  /** Which chips to show. Omit for pages that do not filter by scope. */
  chipKinds = ['category', 'city', 'sku'],
  /** Resolved window echoed by the API, so the date control shows real dates. */
  resolved,
  onRefresh,
  initialScope,
  children,
}: {
  title: string;
  /** Nav child id, for the pages that sit under a group. */
  activeNavId: string;
  /** Nav group id, for the top-level pages that have no children. */
  activeSection?: string;
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
  chipKinds?: ChipKind[];
  resolved?: ResolvedWindow | undefined;
  onRefresh?: () => void;
  initialScope?: Scope;
  children: (ctx: PageShellRender) => ReactNode;
}) {
  const session = loadSession();
  const profiles = useMemo(() => session?.profiles ?? [], [session?.profiles]);
  const [activeProfile, setActiveProfile] = useState<string | null>(() => loadActiveProfile());
  const hasGrants = profiles.length > 0 || (session?.brands?.length ?? 0) > 0;
  const customer = activeProfile ?? (hasGrants ? PREFERRED_CUSTOMER : '');

  const [scope, setScope] = useState<Scope>(initialScope ?? {});
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);

  /**
   * Switching brand clears the scope. A category or SKU that exists in one
   * tenant's catalogue is not a row in another's, and carrying the filter
   * across asks for something the new brand may not sell — an empty page that
   * reads as a data problem rather than a stale filter.
   */
  const changeProfile = useCallback((name: string): void => {
    saveActiveProfile(name);
    setActiveProfile(name);
    setScope({});
  }, []);

  const chips: Chip[] = useMemo(() => {
    const all: Record<ChipKind, Chip> = {
      category: { kind: 'category', label: scope.category ?? 'All Categories', value: scope.category },
      city: { kind: 'city', label: titleCase(scope.city) ?? 'All Cities', value: scope.city },
      sku: { kind: 'sku', label: scope.sku ?? 'All SKUs', value: scope.sku },
    };
    return chipKinds.map((k) => all[k]);
  }, [scope, chipKinds]);

  /** Picking a level sets it and drops the levels it narrows. */
  const selectChip = useCallback((kind: ChipKind, value: string) => {
    setScope((prev) => {
      if (kind === 'category') return { category: value };
      if (kind === 'city') {
        const next: Scope = { city: value };
        if (prev.category !== undefined) next.category = prev.category;
        return next;
      }
      const next: Scope = { sku: value };
      if (prev.category !== undefined) next.category = prev.category;
      if (prev.city !== undefined) next.city = prev.city;
      return next;
    });
  }, []);

  const clearChip = useCallback((kind: ChipKind) => {
    setScope((prev) => {
      if (kind === 'category') return {};
      if (kind === 'city') {
        const next: Scope = {};
        if (prev.category !== undefined) next.category = prev.category;
        return next;
      }
      const next: Scope = {};
      if (prev.category !== undefined) next.category = prev.category;
      if (prev.city !== undefined) next.city = prev.city;
      return next;
    });
  }, []);

  return (
    <div className={styles.shell}>
      <Sidebar
        activeId={activeNavId}
        {...(activeSection ? { activeSection } : {})}
        {...(onNavigate ? { onNavigate } : {})}
      />
      <div className={styles.main}>
        <TopBar
          title={title}
          range={range}
          onRangeChange={setRange}
          {...(resolved ? { resolved } : {})}
          {...(resolved?.end ? { maxDate: resolved.end } : {})}
          {...(session?.user?.fullName || session?.user?.email
            ? { userLabel: String(session.user.fullName ?? session.user.email) }
            : {})}
          profiles={profiles}
          activeProfile={activeProfile}
          onProfileChange={changeProfile}
          {...(onRefresh ? { onSettingsClick: onRefresh } : {})}
          {...(onSignOut ? { onSignOut } : {})}
        />

        {chipKinds.length > 0 && (
          <FilterChips
            chips={chips}
            customerName={customer}
            onSelect={selectChip}
            onClear={clearChip}
            format={{ city: (v) => titleCase(v) ?? v }}
          />
        )}

        <main className={styles.content}>
          {/*
            The window the API actually resolved, when it is not the one that
            was asked for. Rendered HERE rather than per page because every page
            can hit it and none of them should be free to leave it out: a source
            that holds 30 days answering a 60-day request looks identical to a
            quiet week unless the page says which it is.
          */}
          {resolved?.note ? (
            <p className={styles.windowNote} role="status">
              <strong>Window</strong>
              <span>{resolved.note}</span>
            </p>
          ) : null}
          {!hasGrants ? (
            <p className={styles.notice}>
              This sign-in has no Blinkit brands assigned. Ask an administrator to align
              your account, then sign in again.
            </p>
          ) : (
            children({ customer, scope, range, setScope, hasGrants })
          )}
        </main>
      </div>
    </div>
  );
}
